/**
 * LocationAutocomplete — debounced search that hits the same backend
 * endpoints the shipment calculator uses, so Cat 6/7 get the exact same UX:
 *   - mode ∈ {flight} → /api/airports/search  (airport code + coords)
 *   - everything else → /api/locations/search (Google Places + cache)
 *
 * Returns the full Location object (address + lat/lng + type) via onSelect
 * so the caller can ship accurate coords to the backend and skip the
 * extra geocode round-trip.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Plane, Search } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API = `${BACKEND}/api`;

function getToken() {
  return typeof window !== 'undefined' ? localStorage.getItem('token') : null;
}

async function searchLocations(query, mode) {
  if (!query || query.length < 1) return [];
  const isAirport = mode === 'flight';
  const url = isAirport
    ? `${API}/airports/search?query=${encodeURIComponent(query)}`
    : `${API}/locations/search?query=${encodeURIComponent(query)}&location_type=general`;
  const t = getToken();
  const headers = t ? { Authorization: `Bearer ${t}` } : {};
  const res = await fetch(url, { headers });
  if (!res.ok) return [];
  const data = await res.json().catch(() => []);
  if (isAirport) {
    return (data || []).slice(0, 6).map((a) => ({
      address: `${a.name} (${a.code}) — ${a.city}, ${a.country}`,
      latitude: a.latitude,
      longitude: a.longitude,
      type: 'airport',
    }));
  }
  return (data || []).slice(0, 6);
}

export default function LocationAutocomplete({
  label,
  value,                 // Location | null  — selected location
  queryText,             // string          — free-text input
  onQueryChange,         // (text) => void
  onSelect,              // (Location) => void
  mode,                  // scope3 mode — used to pick airport vs general search
  placeholder,
  testIdPrefix = 'loc',
}) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function onDocClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleChange = (e) => {
    const q = e.target.value;
    onQueryChange(q);
    if (timerRef.current) clearTimeout(timerRef.current);

    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    setOpen(true);
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const r = await searchLocations(q, mode);
        setResults(Array.isArray(r) ? r : []);
      } catch (err) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
  };

  const handlePick = (loc) => {
    onSelect(loc);
    onQueryChange(loc.address);
    setResults([]);
    setOpen(false);
  };

  const Icon = mode === 'flight' ? Plane : MapPin;
  const isAirport = mode === 'flight';

  return (
    <div className="relative" ref={containerRef}>
      <Label className="flex items-center gap-1 text-sm font-medium text-gray-700">
        <Icon className="w-3 h-3" /> {label}
      </Label>
      <div className="relative">
        <Input
          value={queryText || ''}
          onChange={handleChange}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder={placeholder || (isAirport ? 'Airport (e.g. LHR or Heathrow)…' : 'City or address…')}
          data-testid={`${testIdPrefix}-input`}
          autoComplete="off"
          className="mt-1 bg-white border-gray-300"
        />
        {loading && (
          <Search className="w-3 h-3 absolute right-2 top-1/2 -translate-y-0.5 text-gray-400 animate-pulse" />
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto">
          {results.map((loc, i) => (
            <div
              key={i}
              onClick={() => handlePick(loc)}
              className="px-3 py-2 text-sm hover:bg-sky-50 cursor-pointer flex items-start gap-2"
              data-testid={`${testIdPrefix}-option-${i}`}
            >
              <Icon className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" />
              <span className="text-gray-800">{loc.address}</span>
            </div>
          ))}
        </div>
      )}
      {value?.latitude != null && value?.longitude != null && (
        <p className="text-[10px] text-emerald-700 mt-1">
          ✓ Resolved: {value.latitude.toFixed(3)}, {value.longitude.toFixed(3)}
        </p>
      )}
    </div>
  );
}
